import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Heading,
  VStack,
  useToast,
  Flex,
  Text,
  Spinner,
  SimpleGrid,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Divider,
  InputGroup,
  InputLeftElement,
  Tooltip,
  Badge,
  HStack,
} from "@chakra-ui/react";
import { BACKEND_URL } from "../../../api/config";
import { FiPlus, FiShield } from "react-icons/fi";
import { SearchIcon, SettingsIcon, InfoIcon } from "@chakra-ui/icons";

function RoleManager() {
  const [roles, setRoles] = useState([]);
  const [roleName, setRoleName] = useState("");
  const [roleDescription, setRoleDescription] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const fetchRoles = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/accounts/account-roles`, {
        credentials: "include",
        cache: "no-store",
      });

      if (!res.ok) throw new Error();
      const data = await res.json();
      setRoles(Array.isArray(data.accountRoles) ? data.accountRoles : []);
    } catch {
      toast({
        title: "Failed to load roles",
        status: "error",
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  const filteredRoles = useMemo(() => {
    if (!searchQuery) return roles;
    return roles.filter((role) => role.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [roles, searchQuery]);

  const handleCreateRole = async () => {
    if (!roleName.trim()) {
      toast({ title: "Role name is required", status: "error" });
      return;
    }

    setCreating(true);

    try {
      const res = await fetch(`${BACKEND_URL}/accounts/account-roles/create`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: roleName.trim(),
          description: "...",
          permissions: [],
        }),
      });

      if (!res.ok) throw new Error();

      toast({
        title: "Role created successfully",
        status: "success",
        position: "top",
      });

      handleClose();
      fetchRoles();
    } catch {
      toast({
        title: "Failed to create role",
        status: "error",
      });
    } finally {
      setCreating(false);
    }
  };

  const handleClose = () => {
    setRoleName("");
    setRoleDescription("");
    onClose();
  };

  return (
    <Box w="100%" py={4}>
      <Flex
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "start", md: "center" }}
        mb={8}
        gap={4}
      >
        <VStack align="start" spacing={1}>
          <Heading size="lg" fontWeight="extrabold" color="gray.700">
            Account Roles
          </Heading>
          <Text color="gray.500" fontSize="sm">
            Define permissions and access levels for child accounts
          </Text>
        </VStack>
        <Button
          leftIcon={<FiPlus />}
          colorScheme="blue"
          size="lg"
          onClick={onOpen}
          shadow="md"
          _hover={{ transform: "translateY(-2px)", shadow: "lg" }}
          transition="all 0.2s"
        >
          Create New Role
        </Button>
      </Flex>

      <Box mb={8}>
        <InputGroup size="lg" maxW="400px">
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder="Search roles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            bg="white"
            borderRadius="full"
            shadow="sm"
          />
        </InputGroup>
      </Box>

      {loading ? (
        <Flex justify="center" align="center" py={20}>
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.500" thickness="4px" />
            <Text color="gray.500" fontWeight="medium">
              Loading access roles...
            </Text>
          </VStack>
        </Flex>
      ) : filteredRoles.length === 0 ? (
        <Box
          textAlign="center"
          py={20}
          bg="gray.50"
          borderRadius="xl"
          border="2px dashed"
          borderColor="gray.200"
        >
          <VStack spacing={4}>
            <FiShield size={48} color="#CBD5E0" />
            <Text color="gray.500" fontSize="lg" fontWeight="medium">
              {searchQuery ? "No roles matching your search" : "No access roles defined yet"}
            </Text>
            {!searchQuery && (
              <Button onClick={onOpen} colorScheme="blue" variant="ghost">
                Initialize your first role
              </Button>
            )}
          </VStack>
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
          {filteredRoles.map((role) => (
            <Box
              key={role.name}
              bg="white"
              p={6}
              borderRadius="2xl"
              shadow="sm"
              border="1px solid"
              borderColor="gray.100"
              _hover={{ shadow: "xl", borderColor: "blue.200", transform: "translateY(-4px)" }}
              transition="all 0.3s cubic-bezier(.08,.52,.52,1)"
              position="relative"
              overflow="hidden"
            >
              <Box position="absolute" top={0} left={0} w="4px" h="100%" bg="blue.500" />

              <Flex justify="space-between" align="start" mb={4}>
                <HStack spacing={4}>
                  <Box p={2} bg="blue.50" borderRadius="lg" color="blue.500">
                    <FiShield size={20} />
                  </Box>
                  <VStack align="start" spacing={0}>
                    <Heading size="md" color="gray.800" noOfLines={1}>
                      {role.name}
                    </Heading>
                    <Text fontSize="xs" color="gray.500">
                      Active Access Role
                    </Text>
                  </VStack>
                  <Tooltip label="Settings">
                    <IconButton
                      size="sm"
                      icon={<SettingsIcon />}
                      aria-label="Settings"
                      variant="ghost"
                      color="gray.400"
                      disabled
                      _hover={{ color: "blue.500", bg: "blue.50" }}
                    />
                  </Tooltip>
                </HStack>
              </Flex>

              <Divider mb={4} />

              <VStack align="start" spacing={3}>
                <HStack justify="space-between" w="100%">
                  <Text fontSize="xs" fontWeight="bold" color="gray.400" textTransform="uppercase">
                    Configured Permissions
                  </Text>
                  <Badge colorScheme="blue" variant="subtle" borderRadius="full" px={2}>
                    {role.permissions?.length || 0}
                  </Badge>
                </HStack>
                <Text fontSize="sm" color="gray.600" noOfLines={2}>
                  {role.description || "General access permissions for this account role level."}
                </Text>
              </VStack>
            </Box>
          ))}
        </SimpleGrid>
      )}

      {/* Role Creation Modal */}
      <Modal isOpen={isOpen} onClose={handleClose} size="lg">
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl">
          <ModalHeader borderBottomWidth="1px">
            <VStack align="start" spacing={0}>
              <Text fontSize="xs" color="blue.500" fontWeight="bold">
                NEW ACCESS CONFIGURATION
              </Text>
              <Text fontSize="xl">Create Account Role</Text>
            </VStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py={6}>
            <VStack spacing={6}>
              <FormControl isRequired>
                <FormLabel fontWeight="bold">Role Title</FormLabel>
                <Input
                  placeholder="e.g. Audit Inspector, Field Manager"
                  value={roleName}
                  onChange={(e) => setRoleName(e.target.value)}
                  size="lg"
                  borderRadius="xl"
                />
              </FormControl>

              <FormControl>
                <FormLabel fontWeight="bold" display="flex" alignItems="center">
                  Description
                  <Tooltip label="Brief summary of what users with this role can do">
                    <Box ml={2} color="blue.400">
                      <InfoIcon size={12} />
                    </Box>
                  </Tooltip>
                </FormLabel>
                <Input
                  placeholder="e.g. Can view certificates and verify scans"
                  value={roleDescription}
                  onChange={(e) => setRoleDescription(e.target.value)}
                  size="lg"
                  borderRadius="xl"
                />
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter borderTopWidth="1px">
            <Button variant="ghost" mr={3} onClick={handleClose} borderRadius="xl">
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleCreateRole}
              isLoading={creating}
              loadingText="Configuring..."
              borderRadius="xl"
              px={8}
            >
              Register Role
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default RoleManager;
